import { FC } from 'react'
import { Form, Input, Select, Switch } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/lib/select';
import { ClientOptionsModel } from 'core/domain/project/model/projectModel';

export enum ProjectCreateFormValuesNames {
  NAME = 'name',
  SUPPORT_EMAILS = 'supportEmails',
  USERS_VERIFIERS = 'usersVerifiers',
  USERS_REQUIRE_VERIFICATION = 'usersRequireVerification',
  TYPE = 'type',
  CLIENT = 'client',
}

export interface ProjectCreateFormValues {
  name: string;
  supportEmails: string[];
  usersVerifiers: string[];
  usersRequireVerification?: boolean;
  type: string;
  client: ClientOptionsModel | null;
}

export interface ProjectCreateFormDataProps {
  form: FormInstance<ProjectCreateFormValues>;
  translations: Record<string, string>;
  typeOptions: DefaultOptionType[];
  clientOptions?: ClientOptionsModel[];
  allowedUsersToVerifyAccounts: DefaultOptionType[];
  onFinish: (values: ProjectCreateFormValues) => void;
  onFinishFailed: () => void;
  validateEmailFormat: (value: string[]) => Promise<void>;
  onSelectClient?: (clientId: string) => Promise<void>;
  onChangeUserVerificationCheck: (value: boolean) => void;
  supportEmailsSeparators: string[];
  isClientSelectLoading?: boolean;
  showClientOptions?: boolean;
  loading: boolean;
  isLoadingUserVerifierList: boolean;
  isUsersRequireVerificationChecked: boolean;
}

const ProjectCreateForm: FC<ProjectCreateFormDataProps> = ({
  translations,
  form,
  allowedUsersToVerifyAccounts,
  typeOptions,
  clientOptions,
  supportEmailsSeparators,
  onFinish,
  onFinishFailed,
  validateEmailFormat,
  onSelectClient,
  onChangeUserVerificationCheck,
  isClientSelectLoading = false,
  showClientOptions = false,
  isLoadingUserVerifierList,
  isUsersRequireVerificationChecked,
  loading,
}) => {
  const {
    nameLabel,
    namePlaceholder,
    nameError,
    supportEmailsLabel,
    supportEmailsPlaceholder,
    usersVerifiersLabel,
    usersVerifiersPlaceholder,
    usersRequireVerificationLabel,
    typeLabel,
    typeError,
    typePlaceholder,
    clientLabel,
    clientPlaceholder,
    clientError,
    userVerifierListError,
  } = translations;
  return (
    <Form
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout={'vertical'}
      requiredMark={false}
    >
      <div>
        <Form.Item
          label={`${nameLabel} *`}
          name={ProjectCreateFormValuesNames.NAME}
          rules={[{ required: true, message: nameError, type: 'string' }]}
        >
          <Input disabled={loading} placeholder={namePlaceholder} />
        </Form.Item>
        <Form.Item
          label={`${typeLabel} *`}
          name={ProjectCreateFormValuesNames.TYPE}
          rules={[{ required: true, message: typeError, type: 'string' }]}
        >
          <Select
            options={typeOptions}
            placeholder={typePlaceholder}
          />
        </Form.Item>
        {showClientOptions && (
          <Form.Item
            label={`${clientLabel} *`}
            name={ProjectCreateFormValuesNames.CLIENT}
            rules={[{ required: showClientOptions, message: clientError }]}
          >
            <Select
              options={clientOptions}
              placeholder={clientPlaceholder}
              optionFilterProp={'label'}
              loading={isClientSelectLoading}
              onChange={(value: ClientOptionsModel) => onSelectClient && onSelectClient(value.value)}
              labelInValue
              showSearch
            />
          </Form.Item>
        )}
        <Form.Item
          name={ProjectCreateFormValuesNames.SUPPORT_EMAILS}
          label={`${supportEmailsLabel} * `}
          rules={[{ validator: (_, value) => validateEmailFormat(value) }]}
        >
          <Select
            mode={'tags'}
            placeholder={supportEmailsPlaceholder}
            tokenSeparators={supportEmailsSeparators}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label={`${usersVerifiersLabel} *`}
          name={ProjectCreateFormValuesNames.USERS_VERIFIERS}
          rules={[{ required: isUsersRequireVerificationChecked, message: userVerifierListError }]}
        >
          <Select
            mode={'tags'}
            options={allowedUsersToVerifyAccounts}
            placeholder={usersVerifiersPlaceholder}
            tokenSeparators={supportEmailsSeparators}
            loading={isLoadingUserVerifierList}
          />
        </Form.Item>
        <Form.Item
          label={usersRequireVerificationLabel}
          name={ProjectCreateFormValuesNames.USERS_REQUIRE_VERIFICATION}
          valuePropName={'checked'}
        >
          <Switch disabled={loading} onChange={onChangeUserVerificationCheck} />
        </Form.Item>
      </div>
    </Form>
  );
}

export default ProjectCreateForm;